// Live data client (point 3). Subscribes to the gateway's LiveHub
// (`/api/live`) for the design's bound variables and receives `sample`
// pushes as new data lands — replacing the runtime's fixed polling. Auth
// reuses the same embed token as the doc channel. Reconnects with backoff.

const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export interface SampleMessage { type: 'sample'; variable_id: string; ts: string; value: number }
// ADR 41 F4 — connector point push from the ConnectorLiveHub.
export interface PointDescriptor { connector_id: string; selection: Record<string, unknown> }
export interface PointSampleMessage { type: 'point_sample'; key: string; connector_id: string; value: number | null; ts: string | null; route: 'live' }
export interface SubscribedPointsMessage { type: 'subscribed_points'; points: Array<{ connector_id: string; selection: Record<string, unknown>; key: string }> }
// ADR 50 — source-agnostic binding subscription. A binding (any kind) is watched
// under a caller-chosen sub_id, echoed back on every value push.
export interface BindingSub { sub_id: string; binding: unknown }
export interface BindingValueMessage { type: 'binding_value'; sub_id: string; value: unknown; ts: string | null }
export interface LiveDataConn {
  close(): void;
  /** Replace the watched variable set (re-sent on reconnect). */
  setVariables(vars: string[]): void;
  /** Replace the watched connector point set (re-sent on reconnect). */
  setPoints(points: PointDescriptor[]): void;
  /** Replace the watched binding set (re-sent on reconnect). */
  setBindings(bindings: BindingSub[]): void;
}

export function connectLiveData(opts: {
  token: string;
  variables: string[];
  onSample: (s: SampleMessage) => void;
  onStatus?: (s: 'open' | 'closed') => void;
  // ADR 41 F4 — connector point live.
  points?: PointDescriptor[];
  onPointSample?: (s: PointSampleMessage) => void;
  onPointsSubscribed?: (list: SubscribedPointsMessage['points']) => void;
  // ADR 50 — source-agnostic binding live.
  bindings?: BindingSub[];
  onBindingValue?: (s: BindingValueMessage) => void;
  /** Override the API base (defaults to VITE_API_BASE or window.origin). */
  base?: string;
}): LiveDataConn {
  const wsBase = ((opts.base || apiBase || window.location.origin)).replace(/^http/, 'ws');
  const url = `${wsBase}/api/live?token=${encodeURIComponent(opts.token)}`;
  let ws: WebSocket | null = null;
  let closed = false;
  let backoff = 1000;
  let vars = [...new Set(opts.variables)].filter(Boolean);
  let points = opts.points ?? [];
  let bindings = opts.bindings ?? [];

  const sendSub = (): void => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (vars.length) ws.send(JSON.stringify({ type: 'subscribe', variables: vars }));
    if (points.length) ws.send(JSON.stringify({ type: 'subscribe_points', points }));
    for (const b of bindings) ws.send(JSON.stringify({ type: 'subscribe_binding', sub_id: b.sub_id, binding: b.binding }));
  };
  const open = (): void => {
    if (closed) return;
    ws = new WebSocket(url);
    ws.onopen = () => { backoff = 1000; opts.onStatus?.('open'); sendSub(); };
    ws.onmessage = (e) => {
      try {
        const m = JSON.parse(String(e.data));
        if (m && m.type === 'sample') opts.onSample(m as SampleMessage);
        else if (m && m.type === 'point_sample') opts.onPointSample?.(m as PointSampleMessage);
        else if (m && m.type === 'subscribed_points') opts.onPointsSubscribed?.((m as SubscribedPointsMessage).points ?? []);
        else if (m && m.type === 'binding_value') opts.onBindingValue?.(m as BindingValueMessage);
      } catch { /* ignore malformed */ }
    };
    ws.onclose = () => {
      opts.onStatus?.('closed');
      if (!closed) { setTimeout(open, backoff); backoff = Math.min(backoff * 2, 15000); }
    };
    ws.onerror = () => ws?.close();
  };
  open();

  return {
    close(): void { closed = true; ws?.close(); },
    setVariables(next): void {
      vars = [...new Set(next)].filter(Boolean);
      sendSub();
    },
    setPoints(next): void {
      points = next ?? [];
      sendSub();
    },
    setBindings(next): void {
      bindings = next ?? [];
      sendSub();
    },
  };
}
